import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { linkageApi } from '../services/api';

interface AUScenarioLinkageDisplayProps {
  bucketId: string;
  environmentId: string;
  packageKey: string;
  auId: string;
  auTitle?: string;
  onEdit?: () => void;
}

export const AUScenarioLinkageDisplay: React.FC<AUScenarioLinkageDisplayProps> = ({
  bucketId,
  environmentId,
  packageKey,
  auId,
  auTitle,
  onEdit,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkage, setLinkage] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const loadLinkage = async () => {
    try {
      setLoading(true);
      setError(null);
      const linkages = await linkageApi.getLinkages(bucketId, environmentId, packageKey);
      setLinkage(linkages[auId] || null);
    } catch (err) {
      console.error('Error loading AU-scenario linkage:', err);
      setError(err instanceof Error ? err.message : 'Failed to load linkage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLinkage();
  }, [bucketId, environmentId, packageKey, auId]);

  const handleRemoveLinkage = async () => {
    if (!linkage) return;

    if (!confirm(`Remove scenario linkage for AU: ${auTitle || auId}?`)) {
      return;
    }

    try {
      setRemoving(true);
      await linkageApi.removeLinkage(bucketId, environmentId, packageKey, auId);
      setLinkage(null);
    } catch (err) {
      console.error('Error removing linkage:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove linkage');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading linkage...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ py: 0, px: 1 }}>
        <Typography variant="caption">{error}</Typography>
      </Alert>
    );
  }

  if (!linkage) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          No scenario linked
        </Typography>
        {onEdit && (
          <IconButton size="small" onClick={onEdit} title="Link scenario">
            <LinkIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  // Parse linkage format: "scenarioUuid:version"
  const [scenarioUuid, version] = linkage.split(':');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <Chip
        label={`${scenarioUuid.slice(0, 8)}... (${version})`}
        size="small"
        color="primary"
        variant="outlined"
        icon={<LinkIcon />}
        title={`Linked to scenario: ${scenarioUuid} version ${version}`}
      />
      {onEdit && (
        <IconButton
          size="small"
          onClick={onEdit}
          disabled={removing}
          title="Edit linkage"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        size="small"
        onClick={handleRemoveLinkage}
        disabled={removing}
        title="Remove linkage"
        color="error"
      >
        {removing ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
};
